import { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { format } from "date-fns";
import {
  CalendarDaysIcon,
  MapPinIcon,
  UserGroupIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import DatePicker from "@/components/common/DatePicker";

/**
 * Der Kopf der Startseite — nach dem Muster der großen Vergleichsportale
 * (booking.com, billiger-mietwagen): ein Bild über die volle Breite, darauf
 * die Überschrift und eine Suchleiste in einer Zeile.
 *
 * Vorher stand hier ein quadratisches Bild, eine Überschrift, drei Häkchen
 * und zwei gleichwertige Knöpfe. Oberhalb des Falzes gab es weder einen
 * Preis noch ein Angebot noch die Möglichkeit, den eigenen Termin zu nennen.
 *
 * Warum die Aufteilung Versand / vor Ort als Reiter ganz oben steht und
 * nicht die PLZ: Der Großteil des Umsatzes läuft über Versand-Fotoboxen,
 * und dafür braucht niemand eine Postleitzahl — die Box kommt überall hin,
 * und der Anbieter fragt sie später ohnehin noch einmal ab. Die PLZ
 * erscheint deshalb nur im Reiter "vor Ort", wo sie tatsächlich entscheidet,
 * wer liefert und was die Anfahrt kostet. Nebenbei erklärt der Reiter das
 * Angebot: Viele wissen nicht, dass man eine Fotobox zuschicken lassen kann.
 */

/**
 * "ca." statt "bis" (Absprache 2026-10-07).
 *
 * "bis 50" verlangt vom Gast eine Umrechnung: Er weiß, dass 55 kommen, und
 * muss überlegen, welche Obergrenze das einschließt. "ca. 50" ist die Zahl,
 * die er ohnehin im Kopf hat — und dieselbe Sprache, die der Vergleich im
 * Filter schon spricht ("ca. 50 Gäste").
 *
 * Die Prüfung dahinter ist unverändert: Wir rechnen, ob der Drucker für so
 * viele Gäste reicht. Wer bei 55 nun "ca. 50" wählt statt "bis 80", bekommt
 * also die knapper passenden Boxen zu sehen statt der reichlich passenden.
 */
const GAESTE_STUFEN = [30, 50, 80, 120, 200, 300];

/**
 * Die Bilder im Hintergrund, im Wechsel nach Anlass.
 *
 * Anforderung an jedes Motiv: Querformat, mindestens 1600 px breit, und das
 * Geschehen gehört ins rechte Drittel — links stehen Überschrift und
 * Suchleiste, dort muss es ruhig und dunkel bleiben.
 *
 * Es fehlen noch Geburtstag und Firmenfeier. Sobald die Dateien unter
 * /public/images/hero/ liegen, kommen sie hier in die Liste — mehr ist es
 * nicht, Wechsel und Nachladen laufen von selbst.
 */
const HINTERGRUND = [
  {
    src: "/images/hero/hochzeit.webp",
    anlass: "Hochzeit",
    alt: "Brautpaar und Gäste lachen auf einer abendlichen Hochzeitsfeier unter Lichterketten",
  },
];

const WECHSEL_MS = 7000;

export default function HeroSuche({
  titel = "Fotobox mieten",
  lead = "Fotoboxen für Hochzeit, Geburtstag oder Firmenfeier — vergleichen und direkt buchen.",
  anzahlBoxen = 0,
  // Eigener Kennzahlen-Satz. Ohne Angabe gilt der Satz der Startseite —
  // dort ist das günstigste Paket eine Versandbox mit kostenloser
  // Lieferung. Auf einer Anlass-Seite kann das ein regionaler Anbieter
  // sein, und dann wäre "inklusive Lieferung" schlicht falsch.
  hinweis = null,
  // Die Startseite trägt die Zahlen seit dem 2026-10-07 an der Liste, nicht
  // im Kopf: Dort standen sie zweimal, zwei Zeilen auseinander.
  ohneHinweis = false,
  anzahlVersand = 0,
  anzahlRegional = 0,
  abPreis = 0,
  // Auf einer Stadtseite ist die Postleitzahl bekannt, und wer dort landet,
  // sucht meist einen Anbieter vor Ort. Beides vorzugeben spart zwei Schritte.
  plzVorgabe = "",
  standardReiter = "versand",
  // Wohin der Knopf führt. Seit der Vergleich auf der Startseite steht
  // (2026-10-07), bleibt man dort und springt nur zur Liste; Stadt- und
  // Anlass-Seiten schicken weiter auf /fotobox-vergleich.
  zielPfad = "/fotobox-vergleich",
  zielAnker = "",
}) {
  const [artDerLieferung, setArtDerLieferung] = useState(standardReiter);
  const [termin, setTermin] = useState(null);
  const [plz, setPlz] = useState(plzVorgabe);
  const [gaeste, setGaeste] = useState(null);
  const [gaesteOffen, setGaesteOffen] = useState(false);
  const [bildIndex, setBildIndex] = useState(0);
  // Die weiteren Motive werden erst nach dem Laden angehängt. Sonst
  // konkurrieren sie mit dem ersten Bild um die Leitung — und das erste ist
  // das größte sichtbare Element der Seite.
  const [weitereBilder, setWeitereBilder] = useState(false);
  const gaesteRef = useRef(null);

  const vorOrt = artDerLieferung === "regional";
  const plzGueltig = /^\d{5}$/.test(plz);

  // Bildwechsel erst nach dem Laden und nur, wenn es etwas zu wechseln gibt.
  // Wer "weniger Bewegung" eingestellt hat, bekommt ein festes Bild — und
  // das erste Bild lädt mit Vorrang, damit der Wechsel nicht den größten
  // sichtbaren Inhalt verzögert.
  useEffect(() => {
    if (HINTERGRUND.length < 2) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const anhaengen = () => setWeitereBilder(true);
    if ("requestIdleCallback" in window) {
      const id = window.requestIdleCallback(anhaengen, { timeout: 3000 });
      var aufraeumen = () => window.cancelIdleCallback(id);
    } else {
      const id = setTimeout(anhaengen, 1500);
      var aufraeumen = () => clearTimeout(id);
    }
    const timer = setInterval(
      () => setBildIndex((i) => (i + 1) % HINTERGRUND.length),
      WECHSEL_MS
    );
    return () => {
      aufraeumen();
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    const zu = (e) => {
      if (gaesteRef.current && !gaesteRef.current.contains(e.target)) {
        setGaesteOffen(false);
      }
    };
    document.addEventListener("mousedown", zu);
    return () => document.removeEventListener("mousedown", zu);
  }, []);

  const ziel = useMemo(() => {
    const p = new URLSearchParams();
    if (termin) p.set("date", format(termin, "yyyy-MM-dd"));
    if (gaeste) p.set("guests", String(gaeste));
    if (vorOrt) {
      p.set("delivery", "regional");
      if (plzGueltig) p.set("postalCode", plz);
    }
    const query = p.toString();
    return query
      ? `${zielPfad}?${query}${zielAnker}`
      : `${zielPfad}${zielAnker}`;
  }, [termin, gaeste, vorOrt, plz, plzGueltig, zielPfad, zielAnker]);

  const feld =
    "flex min-w-0 flex-1 items-center gap-3 rounded-xl px-3.5 py-2.5 text-left transition hover:bg-slate-50 lg:rounded-none lg:py-3";
  const beschriftung =
    "block text-[10px] font-semibold uppercase tracking-wider text-slate-400";
  const wert = "block truncate text-[15px] font-semibold text-slate-900";

  const reiter = (schluessel, text) => {
    const aktiv = artDerLieferung === schluessel;
    return (
      <button
        type="button"
        onClick={() => setArtDerLieferung(schluessel)}
        aria-pressed={aktiv}
        className={`rounded-t-xl px-5 py-2.5 text-sm font-semibold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white ${
          aktiv
            ? "bg-white text-slate-900"
            : "bg-white/15 text-white backdrop-blur-sm hover:bg-white/25"
        }`}
      >
        {text}
      </button>
    );
  };

  return (
    // Kein overflow-hidden auf der Sektion: Kalender und Gästeauswahl
    // klappen nach unten auf und ragen über den Kopf hinaus. Abgeschnitten
    // wird nur das Bild, in seinem eigenen Kasten.
    <section className="relative isolate z-10 bg-slate-900">
      <div className="absolute inset-0 -z-10 overflow-hidden">
        {HINTERGRUND.filter((_, i) => i === 0 || weitereBilder).map((bild, i) => (
          <Image
            key={bild.src}
            src={bild.src}
            alt={i === 0 ? bild.alt : ""}
            aria-hidden={i === 0 ? undefined : true}
            fill
            priority={i === 0}
            quality={70}
            sizes="100vw"
            className={`object-cover object-[70%_35%] transition-opacity duration-1000 ${
              i === bildIndex ? "opacity-100" : "opacity-0"
            }`}
          />
        ))}
        {/* Ohne Abdunklung ist weiße Schrift auf einem Foto Glückssache —
            und das Motiv wechselt, die Schrift bleibt. Zwei Verläufe: einer
            von links, wo der Text steht, einer flach über das ganze Bild,
            damit auch ein helles Motiv trägt. */}
        <div className="absolute inset-0 bg-slate-950/25" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/70 to-slate-950/10" />
      </div>

      <div className="relative z-20 mx-auto max-w-7xl px-4 pb-12 pt-12 sm:px-6 lg:px-8 lg:pb-16 lg:pt-20">
        <h1
          className="max-w-3xl text-[34px] font-bold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl"
          style={{ textShadow: "0 2px 18px rgba(2,6,23,0.45)" }}
        >
          {titel}
        </h1>
        {/* Der erste Fließtext der Seite. Er nennt die drei Anlässe statt
            die Lieferwege — die stehen schon in den Reitern direkt darunter,
            und "Hochzeit, Geburtstag, Firmenfeier" ist das, wonach gesucht
            wird. Länge so gewählt, dass er auf dem Desktop in eine Zeile
            passt und links im abgedunkelten Bereich bleibt. */}
        <p
          className="mt-3 max-w-xl text-base font-medium text-white sm:text-lg lg:max-w-none"
          style={{ textShadow: "0 1px 14px rgba(2,6,23,0.6)" }}
        >
          {lead}
        </p>

        <div className="mt-7 flex gap-1">
          {reiter("versand", "Versand")}
          {reiter("regional", "Anbieter vor Ort")}
        </div>

        <div className="rounded-2xl rounded-tl-none bg-white p-2 shadow-[0_30px_70px_-35px_rgba(2,6,23,0.75)]">
          {/* Sagt in einer Zeile, was der gewählte Weg bedeutet. Viele wissen
              nicht, dass man eine Fotobox einfach zuschicken lassen kann —
              und die Zahl gehört hierher, wo sie eine Frage beantwortet,
              nicht in eine Zeile mit Kennzahlen. */}
          <p className="px-2.5 pb-1 pt-1.5 text-xs text-slate-500">
            {vorOrt
              ? `${anzahlRegional} Anbieter aus deiner Region — die meisten liefern und bauen auf.`
              : `${anzahlVersand} Boxen kommen per Paket — meist die günstigere Wahl.`}
          </p>
          <div className="flex flex-col gap-1 lg:flex-row lg:items-center lg:divide-x lg:divide-slate-200">
            {/* Bewusst ein <div> und kein <label htmlFor>: Der Auslöser des
                Kalenders ist selbst ein Knopf. Ein Label würde den Klick ein
                zweites Mal an ihn weiterreichen — der Kalender ginge auf und
                sofort wieder zu. */}
            <div className={feld}>
              <CalendarDaysIcon className="h-5 w-5 flex-none text-slate-400" />
              <span className="min-w-0 flex-1">
                <span className={beschriftung}>Termin</span>
                <DatePicker
                  id="hero-termin"
                  value={termin}
                  onChange={setTermin}
                  min={new Date()}
                  platzhalter="Wann feierst du?"
                  zeigeIcon={false}
                  triggerClassName={`${wert} w-full bg-transparent text-left ${
                    termin ? "" : "text-slate-400"
                  }`}
                />
              </span>
            </div>

            <div className={`${feld} relative`} ref={gaesteRef}>
              <UserGroupIcon className="h-5 w-5 flex-none text-slate-400" />
              <span className="min-w-0 flex-1">
                <span className={beschriftung}>Gäste</span>
                <button
                  type="button"
                  onClick={() => setGaesteOffen((o) => !o)}
                  aria-expanded={gaesteOffen}
                  className={`${wert} w-full text-left ${
                    gaeste ? "" : "text-slate-400"
                  }`}
                >
                  {gaeste
                    ? gaeste === 300
                      ? "300+ Gäste"
                      : `ca. ${gaeste} Gäste`
                    : "Wie viele kommen?"}
                </button>
              </span>

              {gaesteOffen && (
                <div className="absolute left-0 top-full z-30 mt-2 w-[260px] rounded-xl border border-slate-200 bg-white p-3 shadow-xl">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
                    Gästezahl
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {GAESTE_STUFEN.map((n) => {
                      const aktiv = gaeste === n;
                      return (
                        <button
                          key={n}
                          type="button"
                          onClick={() => {
                            setGaeste(aktiv ? null : n);
                            setGaesteOffen(false);
                          }}
                          aria-pressed={aktiv}
                          className={`flex-none rounded-full px-3 py-1.5 text-sm font-semibold transition ${
                            aktiv
                              ? "bg-slate-900 text-white"
                              : "bg-white text-slate-600 ring-1 ring-slate-200 hover:ring-slate-300"
                          }`}
                        >
                          {n === 300 ? "300+" : `ca. ${n}`}
                        </button>
                      );
                    })}
                  </div>
                  <p className="mt-3 text-xs text-slate-500">
                    Wir prüfen, ob der Drucker für so viele Gäste reicht.
                  </p>
                </div>
              )}
            </div>

            {vorOrt && (
              <label className={feld} htmlFor="hero-plz">
                <MapPinIcon className="h-5 w-5 flex-none text-slate-400" />
                <span className="min-w-0 flex-1">
                  <span className={beschriftung}>Postleitzahl</span>
                  <input
                    id="hero-plz"
                    type="text"
                    inputMode="numeric"
                    autoComplete="postal-code"
                    maxLength={5}
                    placeholder="z. B. 50667"
                    value={plz}
                    onChange={(e) =>
                      setPlz(e.target.value.replace(/\D/g, "").slice(0, 5))
                    }
                    className={`${wert} w-full border-0 bg-transparent p-0 placeholder:font-normal placeholder:text-slate-400 focus:outline-none focus:ring-0`}
                  />
                </span>
              </label>
            )}

            <Link
              href={ziel}
              className="group flex flex-none items-center justify-center gap-2 rounded-xl bg-[#FF5744] px-7 py-3.5 text-base font-semibold text-white shadow-sm transition hover:bg-[#eb4836] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#FF5744] lg:ml-2 lg:mr-1"
            >
              <MagnifyingGlassIcon className="h-5 w-5 flex-none" />
              Fotobox finden
            </Link>
          </div>
        </div>

        {/* Drei Aussagen, jede belegbar. Bewusst nicht dabei:
            "geprüfte Fotoboxen" — im Katalog hat keine einzige Box einen
            Testbericht oder eine eigene Bewertung. Und keine Trustpilot-
            Sterne: Die 31 Bewertungen gehören zum Polaroid-Verleih, nicht
            zur Fotobox-Vermittlung. */}
        <p className={`mt-6 text-sm text-white/80 ${ohneHinweis ? "hidden" : ""}`}>
          {hinweis || (
            <>
              <strong className="font-semibold text-white">
                {anzahlBoxen}
              </strong>{" "}
              Fotoboxen im Vergleich
              {abPreis > 0 && (
                <>
                  , ab{" "}
                  <strong className="font-semibold text-white">
                    {abPreis} €
                  </strong>{" "}
                  inklusive Lieferung
                </>
              )}
              .
            </>
          )}
        </p>
      </div>
    </section>
  );
}
